// --- START OF FILE AnalyticsView.js ---

import React, { useState } from 'react';
import { 
    Check, X, Ban, Shield, Sun, ArrowRightLeft, Clock, MessageSquare, ChevronDown,
    DollarSign, ShieldCheck, Trophy, Calendar, MapPin, ArrowLeft, Home
} from 'lucide-react';
import { ConfirmationModal } from '../shared/ConfirmationModal';

// --- Helper Functions and Constants ---
const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const totalMinutes = Math.floor(seconds / 60);
    if (totalMinutes < 1) return '< 1 min';
    if (totalMinutes === 1) return '1 min';
    return `${totalMinutes} mins`;
};

const formatShotDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const shotResults = [
    { value: 'made', label: 'Made', Icon: Check, color: 'bg-green-500', iconColor: 'text-white' },
    { value: 'miss', label: 'Miss', Icon: X, color: 'bg-red-500', iconColor: 'text-white' },
    { value: 'foul', label: 'Foul', Icon: Ban, color: 'bg-orange-500', iconColor: 'text-white' },
    { value: 'safety', label: 'Safety', Icon: Shield, color: 'bg-blue-500', iconColor: 'text-white' },
    { value: 'dry_break', label: 'Dry Break', Icon: Sun, color: 'bg-gray-500', iconColor: 'text-yellow-300' },
    { value: 'push_out', label: 'Push Out', Icon: ArrowRightLeft, color: 'bg-purple-500', iconColor: 'text-white' },
];

const allTagsWithInfo = [
    { value: 'break', label: 'Break', emoji: '💥' },
    { value: 'masse', label: 'Masse', emoji: '🎩' }, 
    { value: 'jump', label: 'Jump', emoji: '🦘' },
    { value: 'kick', label: 'Kick', emoji: '🦶' }, 
    { value: 'bank', label: 'Bank', emoji: '🏦' },
    { value: 'combo', label: 'Combo', emoji: '🎯' }, 
    { value: 'cut', label: 'Cut Shot', emoji: '📐' },
    { value: 'bridge', label: 'Bridge', emoji: '🌉' },
    { value: 'lucky', label: 'Lucky', emoji: '🍀' },
    { value: 'lucky_safe', label: 'Lucky Safe', emoji: '😇' },
    { value: 'failed_safety', label: 'Failed Safety', emoji: '😬' },
    { value: 'win', label: 'Win', emoji: '🏁' },
    { value: 'break_and_run', label: 'Break & Run', emoji: '⚡️' }, 
    { value: 'ball_in_hand', label: 'Ball in Hand', emoji: '🖐️'}, 
    { value: 'money_ball_shot', label: 'Money Ball Shot', Icon: DollarSign, color: 'text-yellow-400' },
    { value: 'golden_break', label: 'Golden Break', emoji: '👑' }
];

const calculatePlayerStats = (p_shotLog, p_matchData) => {
    const initialStats = { 
        totalShots: 0, 
        successfulShots: 0, 
        ballsMade: 0, 
        safetyCount: 0, 
        misses: 0, 
        fouls: 0, 
        highRun: 0, 
        breakAndRuns: 0, 
        ballInHandShots: 0, 
        missedMoneyBall: 0, 
        breakShots: 0, 
        ballsOnBreak: 0, 
        dryBreaks: 0, 
        goldenBreaks: 0,  // Added golden breaks tracking
        inningsSet: new Set(), 
        shotTypeDistribution: { made: 0, miss: 0, foul: 0, safety: 0, dry_break: 0, push_out: 0 }, 
        distancePerformance: { short: { total: 0, made: 0 }, medium: { total: 0, made: 0 }, long: { total: 0, made: 0 } }, 
        tagAnalysis: {}, 
        gameBreakdown: [], 
        totalDuration: 0,
        breakSuccessRate: 0,  // Added break success rate
        averageBreakBalls: 0, // Added average balls on break
    };
    if (!p_shotLog || !p_matchData) return { player1: initialStats, player2: initialStats, gameBreakdown: [] };
    const stats = { player1: JSON.parse(JSON.stringify(initialStats)), player2: JSON.parse(JSON.stringify(initialStats)), gameBreakdown: [] };
    stats.player1.inningsSet = new Set(); stats.player2.inningsSet = new Set();
    let currentRun = { player1: 0, player2: 0 };
    let gameSlices = [];
    if (p_shotLog.length > 0) {
        let currentSlice = [];
        for(const shot of p_shotLog) {
            currentSlice.push(shot);
            if (shot.isGameWinner) { gameSlices.push(currentSlice); currentSlice = []; }
        }
        if (currentSlice.length > 0) { gameSlices.push(currentSlice); }
    }
    stats.gameBreakdown = gameSlices.map((gameShots, index) => {
        const lastShot = gameShots[gameShots.length - 1];
        const winner = lastShot.isGameWinner ? (lastShot.winnerOfRack === 'player1' ? p_matchData.player1 : p_matchData.player2) : 'In Progress';
        const innings = Math.max(...gameShots.map(s => s.inning));
        const isBreakAndRun = lastShot.tags.includes('break_and_run');
        const duration = gameShots.reduce((sum, s) => sum + (s.duration || 0), 0);
        return { gameNumber: index + 1, winner, innings, shots: gameShots, isBreakAndRun, duration };
    });
    p_shotLog.forEach(shot => {
      const playerHandle = shot.player;
      const playerStats = stats[playerHandle];
      if (!playerStats) return;
      playerStats.totalShots++; playerStats.totalDuration += (shot.duration || 0); playerStats.inningsSet.add(shot.inning);
      if (playerStats.shotTypeDistribution[shot.result] !== undefined) { playerStats.shotTypeDistribution[shot.result]++; }
      if(shot.distance && playerStats.distancePerformance[shot.distance]){ playerStats.distancePerformance[shot.distance].total++; if(shot.result === 'made' && shot.ballsMade.length > 0){ playerStats.distancePerformance[shot.distance].made++; } }
      const uniqueTags = [...new Set(shot.tags)];
      uniqueTags.forEach(tag => {
          if(tag === 'break_and_run') playerStats.breakAndRuns++; 
          else if(tag === 'ball_in_hand') playerStats.ballInHandShots++; 
          else if(tag === 'golden_break') playerStats.goldenBreaks++;  // Track golden breaks
          else if(tag === 'break') {
              playerStats.breakShots++;
              if (shot.ballsMade.length > 0) {
                  playerStats.ballsOnBreak += shot.ballsMade.length;
              } else {
                  playerStats.dryBreaks++;
              }
          }
          else if(tag !== 'break') playerStats.tagAnalysis[tag] = (playerStats.tagAnalysis[tag] || 0) + 1;
      });
      if (shot.result === 'made' && shot.ballsMade.length > 0) {
        playerStats.successfulShots++; currentRun[playerHandle] += shot.ballsMade.length;
        if (currentRun[playerHandle] > playerStats.highRun) { playerStats.highRun = currentRun[playerHandle]; }
      } else { currentRun[playerHandle] = 0; }
      if (shot.result === 'miss') {
          playerStats.misses++;
          if (shot.tags.includes('money_ball_shot')) {
              playerStats.missedMoneyBall++;
          }
      }
      if (shot.result === 'foul') playerStats.fouls++; 
      if (shot.result === 'safety') playerStats.safetyCount++;
    });
    Object.keys(stats).filter(k => k.startsWith('player')).forEach(playerKey => {
        const pStats = stats[playerKey];
        pStats.inningsPlayed = pStats.inningsSet.size; 
        pStats.avgShotsPerInning = pStats.inningsPlayed > 0 ? (pStats.totalShots / pStats.inningsPlayed).toFixed(1) : '0.0'; 
        pStats.successRate = pStats.totalShots > 0 ? ((pStats.successfulShots / pStats.totalShots) * 100).toFixed(1) : '0.0'; 
        pStats.dryBreakRate = pStats.breakShots > 0 ? ((pStats.dryBreaks / pStats.breakShots) * 100).toFixed(1) : '0.0';
        pStats.breakSuccessRate = pStats.breakShots > 0 ? (((pStats.breakShots - pStats.dryBreaks) / pStats.breakShots) * 100).toFixed(1) : '0.0';
        pStats.averageBreakBalls = pStats.breakShots > 0 ? (pStats.ballsOnBreak / pStats.breakShots).toFixed(1) : '0.0';
        const avgDuration = pStats.totalShots > 0 ? pStats.totalDuration / pStats.totalShots : 0;
        pStats.avgShotDuration = formatShotDuration(Math.round(avgDuration));
        Object.keys(pStats.distancePerformance).forEach(dist => { const distPerf = pStats.distancePerformance[dist]; distPerf.successRate = distPerf.total > 0 ? ((distPerf.made / distPerf.total) * 100).toFixed(1) : '0.0'; });
    });
    return stats;
};

const groupShotsByInning = (shots) => {
    if (!shots) return {};
    return shots.reduce((acc, shot) => {
        (acc[shot.inning] = acc[shot.inning] || []).push(shot);
        return acc;
    }, {});
};

const StatCard = ({ title, value, highIsGood = true, isBad = false, icon }) => {
    let valueColor = 'text-white';
    if (parseFloat(value) > 0 && highIsGood && !isBad) valueColor = 'text-green-400';
    if (parseFloat(value) > 0 && isBad) valueColor = 'text-red-400';
    
    // Handle percentage values
    const isPercentage = typeof value === 'string' && value.includes('%');
    const isDuration = typeof value === 'string' && (value.includes('min') || value.includes(':'));
    
    return (
        <div className="bg-slate-700/50 p-2 rounded-lg text-center hover:bg-slate-700/70 transition-colors">
            <div className="text-xs text-slate-400 mb-1 truncate flex items-center justify-center gap-1">
                {icon && <span>{icon}</span>}
                {title}
            </div>
            <div className={`text-lg sm:text-xl font-bold ${valueColor}`}>
                {isPercentage || isDuration ? value : (parseFloat(value) || 0).toLocaleString()}
            </div>
        </div>
    );
};

const PlayerStatsSection = ({ playerName, playerStats, isProfile }) => (
    <div className="bg-slate-800/50 p-3 sm:p-4 rounded-xl border border-slate-700 space-y-4 flex flex-col">
        <div className="flex justify-center items-center gap-2">
            <h3 className="text-lg font-bold text-center truncate">{playerName}</h3>
            {isProfile && <ShieldCheck className="w-5 h-5 text-green-400" title="Registered Player" />}
        </div>
        <div>
            <h4 className="font-semibold text-slate-300 mb-2 text-center text-xs uppercase tracking-wider">Overall</h4>
            <div className="grid grid-cols-3 gap-2">
                <StatCard title="Pot %" value={`${playerStats.successRate}%`} icon="🎯" />
                <StatCard title="High Run" value={playerStats.highRun} icon="🔥" />
                <StatCard title="B & R's" value={playerStats.breakAndRuns} icon="⚡️" />
                <StatCard title="Golden Breaks" value={playerStats.goldenBreaks} icon="👑" />
                <StatCard title="BIH Shots" value={playerStats.ballInHandShots} icon="🖐️" />
                <StatCard title="Fouls" value={playerStats.fouls} isBad={true} icon="⚠️" />
            </div>
        </div>
        
        {/* New Break Performance Section */}
        {playerStats.breakShots > 0 && (
            <div>
                <h4 className="font-semibold text-slate-300 mb-2 text-center text-xs uppercase tracking-wider">Break Performance</h4>
                <div className="grid grid-cols-3 gap-2">
                    <StatCard title="Break Success" value={`${playerStats.breakSuccessRate}%`} icon="💥" />
                    <StatCard title="Avg Balls/Break" value={playerStats.averageBreakBalls} icon="🎱" />
                    <StatCard title="Dry Breaks" value={playerStats.dryBreaks} isBad={true} icon="🌵" />
                </div>
            </div>
        )}
        
        <div>
             <h4 className="font-semibold text-slate-300 mb-2 text-center text-xs uppercase tracking-wider">Shot Distribution</h4>
             <div className="bg-slate-700/20 p-2 rounded-lg space-y-2">
                {Object.entries(playerStats.shotTypeDistribution).map(([type, count]) => {
                     if(count === 0) return null;
                     const resultInfo = shotResults.find(r => r.value === type);
                     const percentage = playerStats.totalShots > 0 ? (count / playerStats.totalShots * 100).toFixed(0) : 0;
                     return (
                         <div key={type} className="text-xs">
                             <div className="flex justify-between items-center mb-1"><span className="capitalize">{resultInfo?.label || type.replace('_', ' ')}</span><span>{count} ({percentage}%)</span></div>
                             <div className="w-full bg-slate-600 rounded-full h-1.5"><div className={`${resultInfo?.color || 'bg-gray-400'} h-1.5 rounded-full`} style={{width: `${percentage}%`}}></div></div>
                         </div>
                     );
                 })}
             </div>
        </div>
        <div>
            <h4 className="font-semibold text-slate-300 mb-2 text-center text-xs uppercase tracking-wider">Pace & Flow</h4>
            <div className="grid grid-cols-3 gap-2">
                <StatCard title="Innings" value={playerStats.inningsPlayed} />
                <StatCard title="Avg Shots/Inning" value={playerStats.avgShotsPerInning} />
                <StatCard title="Avg Shot Time" value={playerStats.avgShotDuration} highIsGood={false} isBad={true}/>
            </div>
        </div>
        {Object.keys(playerStats.tagAnalysis).length > 0 && (
            <div className="flex-grow flex flex-col">
                <h4 className="font-semibold text-slate-300 mb-2 text-center text-xs uppercase tracking-wider">Shot Tag Analysis</h4>
                <div className="flex flex-wrap justify-center gap-2">
                    {Object.entries(playerStats.tagAnalysis).map(([tag, count]) => {
                        const tagInfo = allTagsWithInfo.find(t => t.value === tag);
                        return tagInfo ? (
                             <div key={tag} className="bg-slate-700/50 rounded-full px-3 py-1 text-xs flex items-center gap-2">
                                 <span>{tagInfo.emoji}</span>
                                 <span className="font-medium">{tagInfo.label}</span>
                                 <span className="font-bold text-green-400">{count}</span>
                             </div>
                        ) : null;
                    })}
                </div>
            </div>
        )}
        
        {/* Distance Performance Section */}
        {Object.values(playerStats.distancePerformance).some(dist => dist.total > 0) && (
            <div>
                <h4 className="font-semibold text-slate-300 mb-2 text-center text-xs uppercase tracking-wider">Distance Performance</h4>
                <div className="grid grid-cols-3 gap-2">
                    {Object.entries(playerStats.distancePerformance).map(([distance, stats]) => {
                        if (stats.total === 0) return null;
                        return (
                            <StatCard 
                                key={distance} 
                                title={`${distance.charAt(0).toUpperCase() + distance.slice(1)}`} 
                                value={`${stats.successRate}%`} 
                                highIsGood={true}
                            />
                        );
                    })}
                </div>
            </div>
        )}
    </div>
);

const ShotDetailModal = ({ shot, matchData, onClose }) => {
    if (!shot) return null;
    const getPlayerNameByHandle = (handle) => {
        if (!matchData) return '...';
        return handle === 'player1' ? matchData.player1 : matchData.player2;
    }
    const resultInfo = shotResults.find(r => r.value === shot.result);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-slate-800 rounded-xl p-6 max-w-sm w-full mx-4 border border-slate-700 relative shadow-2xl">
                <button onClick={onClose} className="absolute top-3 right-3 p-1 text-slate-400 hover:text-white rounded-full hover:bg-slate-700 transition-colors"><X size={20} /></button>
                <h2 className="text-xl font-bold mb-4 text-center text-green-400">Shot Details</h2>
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Player:</span><span className="font-bold">{getPlayerNameByHandle(shot.player)}</span></div>
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Inning / Shot:</span><span className="font-bold">{shot.inning} / {shot.shotNumber}</span></div>
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Shot Duration:</span><span className="font-bold">{formatShotDuration(shot.duration)}</span></div>
                    <div className="flex justify-between items-center p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Result:</span><span className="font-bold flex items-center gap-2">{resultInfo?.Icon ? <resultInfo.Icon className="w-4 h-4" /> : null} {resultInfo?.label}</span></div>
                    {shot.ballsMade && shot.ballsMade.length > 0 && ( <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Balls Made:</span><span className="font-bold">{shot.ballsMade.join(', ')}</span></div> )}
                    {shot.pushOutDecision && ( <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Opponent's Push Decision:</span><span className="font-bold capitalize">{shot.pushOutDecision.replace('_', ' ')}</span></div> )}
                    <div className="flex justify-between p-2 bg-slate-700/50 rounded-md"><span className="text-slate-400">Distance:</span><span className="font-bold capitalize">{shot.distance || 'N/A'}</span></div>
                    {shot.notes && ( <div className="pt-2"><h3 className="text-slate-300 mb-1 font-semibold">Notes:</h3><p className="text-sm text-white/80 p-2 bg-slate-700/50 rounded-md">{shot.notes}</p></div> )}
                    {shot.tags && shot.tags.length > 0 && (
                       <div className="pt-2">
                           <h3 className="text-slate-300 mb-2 text-center font-semibold">Tags</h3>
                           <div className="flex flex-wrap justify-center gap-2">
                               {[...new Set(shot.tags)].map(tag => {
                                   const tagInfo = allTagsWithInfo.find(t => t.value === tag);
                                   return tagInfo ? ( <div key={tag} className="bg-slate-700 rounded-full px-3 py-1 text-xs flex items-center gap-2"><span>{tagInfo.emoji}</span><span className="font-medium">{tagInfo.label}</span></div> ) : null;
                               })}
                           </div>
                       </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const MatchResultBanner = ({ matchData, gameState }) => {
    const winnerHandle = gameState.winner;
    const winnerName = winnerHandle === 'player1' ? matchData.player1 : matchData.player2;
    const isPvp = matchData.player2 !== 'Ghost';
    const wasForfeited = gameState.forfeited;
    
    const matchDate = gameState.endTime 
        ? new Date(gameState.endTime).toLocaleDateString() 
        : 'Date not available';

    const totalDurationInSeconds = gameState.startTime && gameState.endTime
        ? Math.floor((new Date(gameState.endTime) - new Date(gameState.startTime)) / 1000)
        : null;

    return (
        <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 mb-4">
            <div className="flex justify-around items-center text-center">
                <div className={`flex flex-col items-center transition-all duration-300 ${winnerHandle === 'player1' ? 'scale-110' : 'opacity-70'}`}>
                    <div className="flex items-center gap-2">
                        {matchData.player1IsProfile && <ShieldCheck className="w-5 h-5 text-green-400" />}
                        <span className={`text-xl font-bold truncate ${winnerHandle === 'player1' ? 'text-green-400' : 'text-white'}`}>{matchData.player1}</span>
                    </div>
                    {winnerHandle === 'player1' && <Trophy className="w-5 h-5 text-yellow-400 mt-1" />}
                </div>
                <div className="font-bold text-4xl sm:text-5xl text-white">
                    <span>{gameState.player1Score}</span>
                    {isPvp && <span className="mx-2 text-slate-500">-</span>}
                    {isPvp && <span>{gameState.player2Score}</span>}
                </div>
                {isPvp && (
                    <div className={`flex flex-col items-center transition-all duration-300 ${winnerHandle === 'player2' ? 'scale-110' : 'opacity-70'}`}>
                        <div className="flex items-center gap-2">
                            <span className={`text-xl font-bold truncate ${winnerHandle === 'player2' ? 'text-green-400' : 'text-white'}`}>{matchData.player2}</span>
                            {matchData.player2IsProfile && <ShieldCheck className="w-5 h-5 text-green-400" />}
                        </div>
                        {winnerHandle === 'player2' && <Trophy className="w-5 h-5 text-yellow-400 mt-1" />}
                    </div>
                )}
            </div>
            <p className="text-center text-sm text-green-400 font-semibold mt-3">
                {wasForfeited ? `${winnerName} wins by forfeit.` : `${winnerName} wins the match!`}
            </p>
            <div className="text-center text-xs text-slate-400 mt-2 flex items-center justify-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                    <Calendar size={12} />
                    <span>{matchDate}</span>
                </div>
                {matchData.location && (
                    <div className="flex items-center gap-1.5">
                        <MapPin size={12} />
                        <span>{matchData.location}</span>
                    </div>
                )}
                {totalDurationInSeconds !== null && (
                    <div className="flex items-center gap-1.5">
                        <Clock size={12} />
                        <span>{formatDuration(totalDurationInSeconds)}</span>
                    </div>
                )}
            </div>
        </div>
    );
};


export const AnalyticsView = ({ shotLog, matchData, isReviewing = false, onBack, abandonMatch, returnHome, gameState: passedGameState }) => {
    const [selectedShot, setSelectedShot] = useState(null);
    const [expandedGame, setExpandedGame] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedGameType, setSelectedGameType] = useState('All');
    const [selectedPlayer, setSelectedPlayer] = useState('All');
    
    if (!matchData || !shotLog) {
        return <div className="flex justify-center items-center h-screen"><p>Loading analytics...</p></div>;
    }
    
    const gameState = passedGameState || {
        player1Score: shotLog.filter(s => s.isGameWinner && s.winnerOfRack === 'player1').length,
        player2Score: shotLog.filter(s => s.isGameWinner && s.winnerOfRack === 'player2').length,
        winner: shotLog.length > 0 && shotLog[shotLog.length - 1].isGameWinner ? shotLog[shotLog.length - 1].winnerOfRack : null,
        startTime: shotLog.length > 0 ? shotLog[0].timestamp : null,
        endTime: shotLog.length > 0 && shotLog[shotLog.length - 1].isGameWinner ? shotLog[shotLog.length - 1].timestamp : null,
        forfeited: shotLog.length > 0 && shotLog[shotLog.length - 1].forfeited,
    };

    // Filter shot log based on selected criteria
    const filteredShotLog = shotLog.filter(shot => {
        if (selectedGameType !== 'All' && matchData.gameType !== selectedGameType) {
            return false;
        }
        if (selectedPlayer !== 'All') {
            const playerName = shot.player === 'player1' ? matchData.player1 : matchData.player2;
            if (playerName !== selectedPlayer) {
                return false;
            }
        }
        return true;
    });

    const stats = calculatePlayerStats(filteredShotLog, matchData);
    const isPvpMode = matchData.player2 !== 'Ghost';
    const showReturnHome = !isReviewing && !!gameState.winner;

    // Calculate match summary stats
    const totalShots = filteredShotLog.length;
    const totalDuration = filteredShotLog.reduce((sum, shot) => sum + (shot.duration || 0), 0);
    const totalGoldenBreaks = filteredShotLog.filter(shot => shot.tags.includes('golden_break')).length;
    const totalBreakAndRuns = filteredShotLog.filter(shot => shot.tags.includes('break_and_run')).length;
    const averageShotsPerGame = stats.gameBreakdown.length > 0 ? (totalShots / stats.gameBreakdown.length).toFixed(1) : '0.0';

    const handleRequestAbandon = () => setShowConfirmModal(true);
    const handleConfirmAbandon = () => {
        setShowConfirmModal(false);
        abandonMatch();
    };

    return (
        <div className="p-2 sm:p-4 max-w-6xl mx-auto">
            <ShotDetailModal shot={selectedShot} matchData={matchData} onClose={() => setSelectedShot(null)} />
            <ConfirmationModal 
                show={showConfirmModal}
                title="End Match?"
                message="Are you sure you want to end the current match and return home?"
                onConfirm={handleConfirmAbandon}
                onClose={() => setShowConfirmModal(false)}
            />
            <div className="flex items-center justify-between mb-4">
                <button onClick={onBack} className="p-2 rounded-full hover:bg-slate-700 -ml-2"><ArrowLeft className="w-6 h-6" /></button>
                <h1 className="text-2xl sm:text-3xl font-bold">Match Analytics</h1>
                {isReviewing ? (
                    <div className="w-10 h-10"></div>
                ) : showReturnHome ? (
                    <button onClick={returnHome} className="p-2 rounded-full hover:bg-slate-700 -mr-2" title="Return Home"><Home className="w-6 h-6" /></button>
                ) : (
                    <button onClick={handleRequestAbandon} className="p-2 rounded-full hover:bg-slate-700 -mr-2" title="End Match & Return Home"><Home className="w-6 h-6" /></button>
                )}
            </div>
            
            {/* Filter Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                <div>
                    <label className="block text-sm font-medium mb-1 text-slate-400">Game Type</label>
                    <select 
                        value={selectedGameType} 
                        onChange={e => setSelectedGameType(e.target.value)} 
                        className="w-full bg-slate-700 p-2 rounded-md text-white"
                    >
                        <option value="All">All Game Types</option>
                        <option value="8-Ball">8-Ball</option>
                        <option value="9-Ball">9-Ball</option>
                        <option value="10-Ball">10-Ball</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1 text-slate-400">Player</label>
                    <select 
                        value={selectedPlayer} 
                        onChange={e => setSelectedPlayer(e.target.value)} 
                        className="w-full bg-slate-700 p-2 rounded-md text-white"
                    >
                        <option value="All">All Players</option>
                        <option value={matchData.player1}>{matchData.player1}</option>
                        {isPvpMode && <option value={matchData.player2}>{matchData.player2}</option>}
                    </select>
                </div>
            </div>
            
            <div className="space-y-4">
                {filteredShotLog && filteredShotLog.length > 0 ? (
                    <>
                        {gameState.winner && (
                            <MatchResultBanner matchData={matchData} gameState={gameState} />
                        )}
                        
                        {/* Match Summary Section */}
                        <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                            <h3 className="text-lg font-bold text-center mb-3 text-slate-300">
                                {selectedGameType !== 'All' ? `${selectedGameType} ` : ''}Match Summary
                                {selectedPlayer !== 'All' ? ` - ${selectedPlayer}` : ''}
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <StatCard title="Total Shots" value={totalShots} icon="🎯" />
                                <StatCard title="Total Duration" value={formatDuration(totalDuration)} highIsGood={false} icon="⏱️" />
                                <StatCard title="Golden Breaks" value={totalGoldenBreaks} icon="👑" />
                                <StatCard title="Break & Runs" value={totalBreakAndRuns} icon="⚡️" />
                                <StatCard title="Games Played" value={stats.gameBreakdown.length} icon="🎮" />
                                <StatCard title="Avg Shots/Game" value={averageShotsPerGame} icon="📊" />
                                <StatCard title="Match Type" value={matchData.gameType} highIsGood={false} icon="🎱" />
                                <StatCard title="Game Mode" value={isPvpMode ? 'PvP' : 'Ghost'} highIsGood={false} icon="👥" />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <PlayerStatsSection playerName={matchData.player1} playerStats={stats.player1} isProfile={matchData.player1IsProfile} />
                            {isPvpMode && <PlayerStatsSection playerName={matchData.player2} playerStats={stats.player2} isProfile={matchData.player2IsProfile} />}
                        </div>
                        <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 space-y-2">
                           <h3 className="text-lg font-bold text-center mb-2">Game Breakdown</h3>
                           <div className="space-y-1">
                               {stats.gameBreakdown.map(game => (
                                 <div key={game.gameNumber} className={`rounded-lg overflow-hidden border border-slate-700 ${game.isBreakAndRun ? 'bg-yellow-500/10 border-yellow-500/30' : ''}`}>
                                    <div onClick={() => setExpandedGame(expandedGame === game.gameNumber ? null : game.gameNumber)} className="flex items-center bg-slate-700/50 p-2 cursor-pointer hover:bg-slate-700 transition-colors">
                                        <div className="font-bold pr-3 text-slate-300">G{game.gameNumber}</div>
                                        <div className="flex-grow text-left"><div className="font-semibold truncate">{game.winner}</div><div className="text-xs text-slate-400">{game.innings} Innings • {formatShotDuration(game.duration)}</div></div>
                                        <div className="flex justify-end items-center pl-2">
                                            {game.isBreakAndRun && <span className="text-yellow-400 text-lg mr-2" title="Break and Run">⚡️</span>}
                                            {game.shots.some(shot => shot.tags.includes('golden_break')) && <span className="text-yellow-400 text-lg mr-2" title="Golden Break">👑</span>}
                                            <ChevronDown className={`w-5 h-5 transition-transform ${expandedGame === game.gameNumber ? 'rotate-180' : ''}`} />
                                        </div>
                                    </div>
                                   {expandedGame === game.gameNumber && (
                                       <div className="p-2 border-t border-slate-600/50 bg-slate-900/50 rounded-b-lg max-h-64 overflow-y-auto">
                                            {Object.entries(groupShotsByInning(game.shots)).reverse().map(([inning, shots]) => (
                                                <div key={inning}>
                                                    <div className="font-bold text-xs text-green-400 my-2 ml-1">Inning {inning}</div>
                                                    {shots.map(shot => {
                                                        const resultInfo = shotResults.find(r => r.value === shot.result);
                                                        const ResultIcon = resultInfo ? resultInfo.Icon : null;
                                                        return (
                                                         <div key={shot.id} onClick={() => setSelectedShot(shot)} className="flex items-center justify-between py-1 text-xs border-b border-slate-700/50 last:border-b-0 cursor-pointer hover:bg-slate-700/50 rounded px-2 -mx-2">
                                                            <div className="flex items-center gap-2"><span className="w-5 text-center font-mono text-slate-400">#{shot.shotNumber}</span><span className="font-medium truncate">{shot.player === 'player1' ? matchData.player1 : matchData.player2}</span></div>
                                                            <div className="flex items-center gap-1.5">
                                                                {shot.ballsMade.length > 0 && <div className="text-[10px] bg-yellow-400/20 text-yellow-300 px-1.5 py-0.5 rounded-full">{shot.ballsMade.join(',')}</div> }
                                                                {[...new Set(shot.tags)].map(tag => { 
                                                                    const tagInfo = allTagsWithInfo.find(t => t.value === tag); 
                                                                    if (!tagInfo) return null;
                                                                    if (tagInfo.Icon) {
                                                                        return <tagInfo.Icon key={tag} title={tagInfo.label} size={14} className={tagInfo.color || ''} />;
                                                                    }
                                                                    if (tagInfo.emoji) {
                                                                        return <span key={tag} title={tagInfo.label} className="text-md">{tagInfo.emoji}</span>;
                                                                    }
                                                                    return null;
                                                                })}
                                                                {shot.notes && <MessageSquare size={12} className="text-blue-400"/>}
                                                                {shot.duration !== undefined && ( <div className="flex items-center gap-1 text-[10px] text-slate-400 bg-slate-800/50 px-1.5 py-0.5 rounded-full"><Clock size={10} /><span>{formatShotDuration(shot.duration)}</span></div> )}
                                                                {ResultIcon && <ResultIcon className={`w-4 h-4 ${resultInfo.iconColor}`} />}
                                                            </div>
                                                        </div>
                                                    )})}
                                                </div>
                                            ))}
                                        </div>
                                   )}
                                 </div>
                               ))}
                           </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center bg-slate-800/50 p-8 rounded-xl border border-slate-700">
                        <h3 className="text-xl font-bold">No shots found for selected filters.</h3>
                        <p className="text-slate-400 mt-2">Try adjusting your filter criteria.</p>
                    </div>
                )}
            </div>
        </div>
    );
};